package com.evoapps.evoshift.observability

/**
 * Crash reports (Firebase Crashlytics) and usage statistics (Google Analytics for Firebase), SH-86.
 *
 * Both are off until the person allows them ([PrivacyConsent]); debug builds, tests and the
 * emulator setups never collect, whatever was chosen ([TelemetryPlatform.canCollect]). Nothing
 * here may carry personal data: the user id is the Firebase uid only, events come from the fixed
 * catalogue in [AnalyticsEvent] and their parameters are filtered by [AnalyticsEvent.ALLOWED_PARAMS].
 * See docs/observability.md.
 */
interface Telemetry {
    /** Applies the person's choice (and, at start, the stored one). */
    fun setCollection(analytics: Boolean, crashes: Boolean)
    fun logEvent(event: AnalyticsEvent)
    fun recordNonFatal(throwable: Throwable)
    /** The Firebase uid of the signed-in person, null when signed out. Never a name or e-mail. */
    fun setUserId(uid: String?)
    /** Context attached to crash reports: the role at the current workplace and how many workplaces. */
    fun setCrashContext(role: String?, workplaceCount: Int)

    /** Collects nothing (previews, before the DI graph exists). */
    object None : Telemetry {
        override fun setCollection(analytics: Boolean, crashes: Boolean) = Unit
        override fun logEvent(event: AnalyticsEvent) = Unit
        override fun recordNonFatal(throwable: Throwable) = Unit
        override fun setUserId(uid: String?) = Unit
        override fun setCrashContext(role: String?, workplaceCount: Int) = Unit
    }
}

/**
 * The platform's Firebase SDKs (Android: firebase-analytics / firebase-crashlytics; iOS: the Swift
 * bridge in iosApp; web: the Analytics JS SDK, no Crashlytics). Only [GatedTelemetry] talks to it,
 * so a backend never has to check consent itself.
 */
interface TelemetryBackend {
    /**
     * Turns the SDKs' collection on or off. [initial]: applying the stored choice at app start (the
     * first call); otherwise the person has just changed it. A crash consent given now discards the
     * reports kept from before it; turning crashes off discards the unsent ones; withdrawing
     * analytics also resets its app-instance id.
     */
    fun setCollection(analytics: Boolean, crashes: Boolean, initial: Boolean)
    fun logEvent(name: String, params: Map<String, String>)
    fun recordNonFatal(throwable: Throwable)
    /** Sets (or with null clears) the user id of both SDKs. */
    fun setUserId(uid: String?)
    fun setCrashKey(key: String, value: String)
}

object NoOpTelemetryBackend : TelemetryBackend {
    override fun setCollection(analytics: Boolean, crashes: Boolean, initial: Boolean) = Unit
    override fun logEvent(name: String, params: Map<String, String>) = Unit
    override fun recordNonFatal(throwable: Throwable) = Unit
    override fun setUserId(uid: String?) = Unit
    override fun setCrashKey(key: String, value: String) = Unit
}

/**
 * What this build and platform allow. [canCollect] is false in debug builds, on localhost and in
 * the emulator setups: then nothing is ever sent and the first-run question is not asked.
 * [supportsCrashReports] is false on the web (Crashlytics has no web SDK), which hides that switch.
 */
data class TelemetryPlatform(val canCollect: Boolean, val supportsCrashReports: Boolean = true)

/**
 * The consent gate in front of a [TelemetryBackend]: events only while usage statistics are allowed,
 * non-fatals and the user id only while crash reports (or statistics) are allowed, and nothing at all
 * when the build cannot collect.
 */
class GatedTelemetry(
    private val backend: TelemetryBackend,
    private val canCollect: Boolean
) : Telemetry {
    var analyticsEnabled: Boolean = false
        private set
    var crashesEnabled: Boolean = false
        private set
    private var applied = false
    private var uid: String? = null
    private var lastRecorded: Throwable? = null

    override fun setCollection(analytics: Boolean, crashes: Boolean) {
        val wasEnabled = analyticsEnabled || crashesEnabled
        analyticsEnabled = analytics && canCollect
        crashesEnabled = crashes && canCollect
        // Always passed on, also as "off": the SDKs persist an earlier "on" across launches.
        backend.setCollection(analyticsEnabled, crashesEnabled, initial = !applied)
        applied = true
        val enabled = analyticsEnabled || crashesEnabled
        if (enabled && uid != null) backend.setUserId(uid)
        if (!enabled && wasEnabled) backend.setUserId(null)
    }

    override fun logEvent(event: AnalyticsEvent) {
        if (!analyticsEnabled) return
        backend.logEvent(event.name, AnalyticsEvent.sanitize(event.params))
    }

    override fun recordNonFatal(throwable: Throwable) {
        if (!crashesEnabled) return
        // One failure is often turned into a message more than once (state and snackbar).
        if (throwable === lastRecorded) return
        lastRecorded = throwable
        backend.recordNonFatal(throwable)
    }

    override fun setUserId(uid: String?) {
        if (this.uid == uid) return
        this.uid = uid
        if (analyticsEnabled || crashesEnabled) backend.setUserId(uid)
    }

    override fun setCrashContext(role: String?, workplaceCount: Int) {
        if (!crashesEnabled) return
        backend.setCrashKey("role", role ?: "none")
        backend.setCrashKey("workplace_count", workplaceCount.toString())
    }
}

/**
 * The app's [Telemetry] for code without DI (view models' events, [toUiText][com.evoapps.evoshift.ui.util.toUiText]'s
 * non-fatals). Installed when the DI graph builds its [Telemetry]; [Telemetry.None] until then.
 */
object AppTelemetry {
    var current: Telemetry = Telemetry.None
        private set

    fun install(telemetry: Telemetry) {
        current = telemetry
        // Best-effort domain writes that failed (a cost snapshot) reach the same non-fatals.
        com.evoapps.evoshift.domain.util.DomainErrors.install { telemetry.recordNonFatal(it) }
        // Firestore listeners the server ended (data layer, SafeListeners): a non-fatal, not a crash.
        com.evoapps.evoshift.data.remote.ListenerErrors.reporter = { e -> current.recordNonFatal(e) }
    }

    fun log(event: AnalyticsEvent) = current.logEvent(event)

    fun recordNonFatal(throwable: Throwable) = current.recordNonFatal(throwable)
}
